import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { confirmAction, confirmReturnToQueue, confirmStartPatientSession } from '../../utils/confirmAction';
import { useClinicRoutingOptions } from '../../hooks/useClinicRoutingOptions';
import { startQueueEntry, releaseQueueEntry } from '../../api/queue';
import { getHandoverVitals, recordScreeningNurseAssessmentAndPush, dischargeScreeningNursePatient } from '../../api/vitals';
import DischargePatientSection from '../../components/consultation/DischargePatientSection';
import {
  dischargeConfirmText,
  DISCHARGE_CONFIRM_TITLE,
  validateRefusalDischargeReason,
} from '../../utils/dischargeDocumentation';
import ActiveSessionQueueAside from '../../components/queue/ActiveSessionQueueAside';
import QueueEntryCard from '../../components/queue/QueueEntryCard';
import { sortQueueEmergencyFirst } from '../../utils/queueDisplay';
import { nurse as c } from '../nurse/styles/nurseClasses';
import ParameterHandoverPanel from './components/ParameterHandoverPanel';
import ScreeningAssessmentForm from './components/ScreeningAssessmentForm';
import ScreeningNurseTopbar from './components/ScreeningNurseTopbar';
import {
  useScreeningNurseQueue,
  useScreeningNurseSession,
  pickAutoResumeEntry,
} from './hooks/useScreeningNurseQueue';
import {
  emptyScreeningForm,
  validateScreeningForm,
  buildScreeningPayload,
  buildScreeningDischargePayload,
  isScreeningFormComplete,
  SCREENING_DESTINATIONS,
} from './screeningNurseForm';

const KOPANO = 'https://kopanovertex.com/';

function QueueEmptyIcon() {
  return (
    <svg width="72" height="72" viewBox="0 0 24 24" fill="none" aria-hidden className="text-slate-300">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17 10h-1V7a4 4 0 10-8 0v3H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2v-8a2 2 0 00-2-2zm-3 0h-4V7a2 2 0 114 0v3z" />
    </svg>
  );
}

export default function ScreeningNursePage() {
  const { nurseLabel, initials, userId } = useScreeningNurseSession();
  const { options: routingOptions } = useClinicRoutingOptions();
  const screeningDestinations = useMemo(() => {
    const api = routingOptions?.screening_nurse;
    if (!api?.length) return SCREENING_DESTINATIONS;
    return api.map((d) => {
      const full = SCREENING_DESTINATIONS.find((s) => s.value === d.value);
      return full ? { ...full, label: d.label || full.label } : d;
    });
  }, [routingOptions]);
  const [queueSearch, setQueueSearch] = useState('');
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [form, setForm] = useState(emptyScreeningForm);
  const [handoverVitals, setHandoverVitals] = useState(null);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [queueActionError, setQueueActionError] = useState('');
  const skipAutoResumeRef = useRef(false);

  const onQueueSynced = useCallback(
    (mapped) => {
      if (skipAutoResumeRef.current) {
        skipAutoResumeRef.current = false;
        return;
      }
      const mine = pickAutoResumeEntry(mapped, userId);
      if (mine) setActiveEntryId((prev) => prev || mine.entryId);
    },
    [userId]
  );

  const { queue, setQueue, loading, error: queueLoadError, live, refresh } = useScreeningNurseQueue({
    onQueueSynced,
  });

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!activeEntryId || !userId) return;
    const still = queue.find(
      (p) =>
        p.entryId === activeEntryId
        && p.status === 'in_progress'
        && p.assignedToId === userId
    );
    if (!still) setActiveEntryId(null);
  }, [queue, activeEntryId, userId]);

  const activePatient = useMemo(
    () => queue.find((p) => p.entryId === activeEntryId) || null,
    [queue, activeEntryId]
  );

  useEffect(() => {
    if (!activePatient?.visitId) {
      setHandoverVitals(null);
      return undefined;
    }
    let cancelled = false;
    setHandoverLoading(true);
    getHandoverVitals(activePatient.visitId)
      .then((data) => {
        if (!cancelled) setHandoverVitals(data);
      })
      .catch(() => {
        if (!cancelled) setHandoverVitals(null);
      })
      .finally(() => {
        if (!cancelled) setHandoverLoading(false);
      });
    return () => { cancelled = true; };
  }, [activePatient?.visitId]);

  const filteredQueue = useMemo(() => {
    const q = queueSearch.trim().toLowerCase();
    const list = q
      ? queue.filter(
        (p) =>
          p.name.toLowerCase().includes(q)
          || p.patientIdLabel.toLowerCase().includes(q)
          || p.patient?.patient_number?.toLowerCase().includes(q)
      )
      : queue;
    return sortQueueEmergencyFirst(list);
  }, [queue, queueSearch]);

  const workspaceActive =
    activePatient
    && activePatient.status === 'in_progress'
    && activePatient.assignedToId === userId;

  const canSubmit = isScreeningFormComplete(form, screeningDestinations);

  function isLockedToOther(patient) {
    return (
      patient.status === 'in_progress'
      && patient.assignedToId
      && patient.assignedToId !== userId
    );
  }

  async function handleSelectPatient(patient) {
    if (isLockedToOther(patient) || actionLoading) return;
    if (workspaceActive && patient.entryId !== activeEntryId) return;

    const starting = patient.status === 'pending';
    if (!(await confirmStartPatientSession(patient.name, starting))) return;

    setActionLoading(true);
    setQueueActionError('');
    try {
      if (patient.status === 'pending') {
        await startQueueEntry(patient.entryId);
        await refresh();
        setForm(emptyScreeningForm());
        setFieldErrors({});
        setSubmitError('');
      }
      setActiveEntryId(patient.entryId);
    } catch (err) {
      setQueueActionError(err.message || 'Could not open patient');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSubmit() {
    if (!activePatient || actionLoading) return;

    const validation = validateScreeningForm(form, screeningDestinations);
    if (Object.keys(validation).length > 0) {
      setFieldErrors(validation);
      return;
    }

    const destLabel = screeningDestinations.find((d) => d.value === form.routing_destination)?.label;
    if (!(await confirmAction({
      title: 'Submit assessment?',
      text: `Save screening assessment and route ${activePatient.name} to ${destLabel || form.routing_destination}?`,
      icon: 'question',
      confirmButtonText: 'Submit & route',
    }))) return;

    setActionLoading(true);
    setSubmitError('');
    setFieldErrors({});
    try {
      const body = buildScreeningPayload(form, {
        visitId: activePatient.visitId,
        queueEntryId: activePatient.entryId,
      });

      const completedEntryId = activePatient.entryId;
      skipAutoResumeRef.current = true;
      setActiveEntryId(null);
      setForm(emptyScreeningForm());
      setHandoverVitals(null);

      await recordScreeningNurseAssessmentAndPush(body);

      setQueue((prev) => prev.filter((p) => p.entryId !== completedEntryId));
      setToast(`${activePatient.name} routed to ${destLabel || form.routing_destination}`);
      await refresh();
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit assessment');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDischarge() {
    if (!activePatient || actionLoading) return;

    const validation = validateRefusalDischargeReason(form.discharge_reason);
    if (Object.keys(validation).length > 0) {
      setFieldErrors(validation);
      return;
    }

    if (!(await confirmAction({
      title: DISCHARGE_CONFIRM_TITLE,
      text: dischargeConfirmText(activePatient.name),
      icon: 'warning',
      confirmButtonText: 'End consultation',
    }))) return;

    setActionLoading(true);
    setSubmitError('');
    setFieldErrors({});
    try {
      const body = buildScreeningDischargePayload(form, {
        visitId: activePatient.visitId,
        queueEntryId: activePatient.entryId,
      });

      const completedEntryId = activePatient.entryId;
      skipAutoResumeRef.current = true;
      setActiveEntryId(null);
      setForm(emptyScreeningForm());
      setHandoverVitals(null);

      await dischargeScreeningNursePatient(body);

      setQueue((prev) => prev.filter((p) => p.entryId !== completedEntryId));
      setToast(`${activePatient.name} — refusal documented, consultation ended`);
      await refresh();
    } catch (err) {
      setSubmitError(err.message || 'Failed to discharge patient');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReturnToQueue() {
    if (!activePatient || actionLoading) return;
    if (!(await confirmReturnToQueue(activePatient.name, 'Unsaved assessment will be discarded.'))) {
      return;
    }

    setActionLoading(true);
    setSubmitError('');
    try {
      skipAutoResumeRef.current = true;
      await releaseQueueEntry(activePatient.entryId);
      setActiveEntryId(null);
      setForm(emptyScreeningForm());
      setHandoverVitals(null);
      setFieldErrors({});
      setToast(`${activePatient.name} returned to queue`);
      await refresh();
    } catch (err) {
      setSubmitError(err.message || 'Could not return patient to queue');
    } finally {
      setActionLoading(false);
    }
  }

  function handleFieldChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSubmitError('');
  }

  function renderBadge(patient) {
    if (patient.isEmergency) {
      return <span className={c.badgeEmergency}>Emergency</span>;
    }
    if (patient.status === 'in_progress') {
      return (
        <span className={c.badgeProgress}>
          In progress
          {patient.assignedToId === userId ? (
            <span className={c.lockTag}><LockIcon /> You</span>
          ) : patient.assignedToName ? (
            <span className={c.lockTag}><LockIcon /> {patient.assignedToName}</span>
          ) : null}
        </span>
      );
    }
    return <span className={c.badgePending}>Waiting</span>;
  }

  return (
    <div className={c.page}>
      <ScreeningNurseTopbar nurseLabel={nurseLabel} initials={initials} live={live} />

      {toast ? (
        <div className={c.toast} role="status">{toast}</div>
      ) : null}

      <div className={c.body}>
        <aside className={c.queueAside} aria-label="Screening queue">
          <h2 className={c.queueTitle}>Screening queue</h2>
          {workspaceActive ? (
            <p className={c.queueSub}>One patient at a time — finish or return to queue</p>
          ) : (
            <p className={c.queueSub}>
              <span className={c.queueCount}>{queue.length}</span> patient{queue.length === 1 ? '' : 's'} waiting
            </p>
          )}

          {workspaceActive ? (
            <ActiveSessionQueueAside
              classes={c}
              badge="In progress"
              title="Active screening session"
              message="Complete assessment and route, or return the patient to the waiting queue."
            />
          ) : (
            <>
              <div className={c.searchWrap}>
                <label htmlFor="sn-queue-search" className="sr-only">Search queue</label>
                <input
                  id="sn-queue-search"
                  type="search"
                  className={c.searchInput}
                  placeholder="Search by name or patient ID…"
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {queueLoadError ? (
                <p className={`${c.hint} text-red-600`} role="alert">{queueLoadError}</p>
              ) : null}
              {queueActionError ? (
                <p className={`${c.hint} mt-1 text-red-600`} role="alert">{queueActionError}</p>
              ) : null}
              <div className={c.queueList}>
                {loading ? (
                  <p className={c.hint}>Loading queue…</p>
                ) : filteredQueue.length === 0 ? (
                  <p className={c.hint}>
                    {queueSearch.trim() ? 'No patients match your search.' : 'No patients in the screening queue.'}
                  </p>
                ) : (
                  filteredQueue.map((p) => (
                    <QueueEntryCard
                      key={p.entryId}
                      classes={c}
                      name={p.name}
                      meta={p.sexAge}
                      idLabel={p.patientIdLabel}
                      badge={renderBadge(p)}
                      active={p.entryId === activeEntryId}
                      locked={isLockedToOther(p)}
                      emergency={p.isEmergency}
                      onClick={() => handleSelectPatient(p)}
                      lockedLabel="Locked by another nurse"
                    />
                  ))
                )}
              </div>
            </>
          )}
        </aside>

        <div className={c.main}>
          {!workspaceActive ? (
            <div className={c.idle} role="region" aria-label="Screening workspace">
              <QueueEmptyIcon />
              <h3 className={c.idleTitle}>No patient selected</h3>
              <p className={c.idleText}>
                Select a patient from the screening queue. Parameter Nurse vitals will appear read-only in the detail view.
              </p>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className={`${c.banner} shrink-0`}>
                <div>
                  <span className={c.bannerLabel}>Active patient</span>
                  <strong className={c.bannerValue}>{activePatient.name}</strong>
                </div>
                <div>
                  <span className={c.bannerLabel}>Demographics</span>
                  <strong className={c.bannerValue}>{activePatient.sexAge}</strong>
                </div>
                <div>
                  <span className={c.bannerLabel}>Patient ID</span>
                  <strong className={c.bannerValue}>
                    {activePatient.patientIdLabel.replace('ID: ', '')}
                  </strong>
                </div>
              </div>

              <div className={c.formScroll}>
                <ParameterHandoverPanel vitals={handoverVitals} loading={handoverLoading} />

                <ScreeningAssessmentForm
                  form={form}
                  fieldErrors={fieldErrors}
                  onFieldChange={handleFieldChange}
                  canSubmit={canSubmit}
                  onSubmit={handleSubmit}
                  actionLoading={actionLoading}
                  destinations={screeningDestinations}
                />

                <DischargePatientSection
                  idPrefix="sn"
                  dischargeReason={form.discharge_reason}
                  onDischargeReasonChange={(value) => handleFieldChange('discharge_reason', value)}
                  error={fieldErrors.discharge_reason}
                  actionLoading={actionLoading}
                  onDischarge={handleDischarge}
                />

                <div className="mt-4 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    className={c.btnSecondary}
                    disabled={actionLoading}
                    onClick={handleReturnToQueue}
                  >
                    Return to queue
                  </button>
                </div>
                {submitError ? (
                  <p className={c.submitError} role="alert">{submitError}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className={c.footer}>
        Health Management System | A digital solution by{' '}
        <a href={KOPANO} target="_blank" rel="noopener noreferrer" className={c.footerLink}>
          Kopano-Vertex
        </a>{' '}
        | Screening Nurse module
      </footer>
    </div>
  );
}
